// Section 22 History tab: charts over the run (population, food, water, wood, tools, trust,
// happiness, fragility, inequality) with event and death markers, and the season-by-season
// report archive (the last 12 in full with their factor lists, older seasons as summaries).
import {
  narrate,
  type Factor,
  type GameState,
  type Metric,
  type Narration,
  type ReportEntry,
} from '../sim/api';
import { stampOfTurn, type SeasonStamp } from './calendar';
import { memo } from './memo';
import { deathLine, type DeathLine } from './report';

export type ChartMetric = Extract<
  Metric,
  'population' | 'food' | 'water' | 'wood' | 'tools' | 'trust' | 'happiness' | 'fragility' | 'gini'
>;

export const CHART_METRICS: readonly ChartMetric[] = [
  'population',
  'food',
  'water',
  'wood',
  'tools',
  'trust',
  'happiness',
  'fragility',
  'gini',
];

export interface ChartMarker {
  /** Report turn (series index + 1). */
  turn: number;
  kind: 'event' | 'death';
  /** Event ids or names of the dead; empty for compacted seasons. */
  labels: string[];
  count: number;
}

export interface ChartVM {
  metric: ChartMetric;
  values: number[];
  /** Turn of values[0] (always 1: series start with the first season). */
  firstTurn: number;
  last: number | null;
  min: number;
  max: number;
}

export interface ArchiveFull extends SeasonStamp {
  kind: 'full';
  population: { start: number; end: number };
  fragility: number;
  fragilityFactors: Factor[];
  deaths: DeathLine[];
  lines: Narration[];
  /** Per-job production with the factor list of a representative worker. */
  production: { narration: Narration; factors: Factor[] }[];
}

export interface ArchiveSummary extends SeasonStamp {
  kind: 'summary';
  population: number;
  births: number;
  deaths: number;
  fragility: number;
  events: number;
  keyLines: Narration[];
}

export interface HistoryVM {
  charts: ChartVM[];
  markers: ChartMarker[];
  /** Newest first. */
  archive: (ArchiveFull | ArchiveSummary)[];
  seasons: number;
}

function markersOf(game: GameState): ChartMarker[] {
  const out: ChartMarker[] = [];
  const add = (turn: number, kind: ChartMarker['kind'], labels: string[], count: number): void => {
    if (count > 0) out.push({ turn, kind, labels, count });
  };
  for (const s of game.historySummaries) {
    add(s.turn, 'event', [], s.entryCounts.event ?? 0);
    const dead = s.keyEntries.flatMap((e) => (e.kind === 'death' ? [e.name] : []));
    add(s.turn, 'death', dead, dead.length);
  }
  for (const r of game.history) {
    const events = r.entries.flatMap((e) => (e.kind === 'event' ? [e.eventId] : []));
    add(r.turn, 'event', events, events.length);
    const dead = r.entries.flatMap((e) => (e.kind === 'death' ? [e.name] : []));
    add(r.turn, 'death', dead, dead.length);
  }
  return out;
}

const narrated = (entries: readonly ReportEntry[], game: GameState): Narration[] =>
  entries.flatMap((e) => {
    const n = narrate(e, game);
    return n === null ? [] : [n];
  });

export const historyVM = memo((game: GameState): HistoryVM => {
  const charts = CHART_METRICS.map((metric): ChartVM => {
    const values = game.series[metric];
    return {
      metric,
      values,
      firstTurn: 1,
      last: values[values.length - 1] ?? null,
      min: values.length === 0 ? 0 : Math.min(...values),
      max: values.length === 0 ? 0 : Math.max(...values),
    };
  });
  const full = game.history.map((r): ArchiveFull => {
    return {
      ...stampOfTurn(r.turn),
      kind: 'full',
      population: { start: r.population.start, end: r.population.end },
      fragility: r.fragility,
      fragilityFactors: r.fragilityFactors,
      deaths: r.entries.flatMap((e) => (e.kind === 'death' ? [deathLine(e)] : [])),
      lines: narrated(
        r.entries.filter((e) => e.kind !== 'death' && e.kind !== 'production'),
        game,
      ),
      production: r.entries.flatMap((e) => {
        if (e.kind !== 'production') return [];
        const n = narrate(e, game);
        return n === null ? [] : [{ narration: n, factors: e.factors }];
      }),
    };
  });
  const summaries = game.historySummaries.map(
    (s): ArchiveSummary => ({
      ...stampOfTurn(s.turn),
      kind: 'summary',
      population: s.population,
      births: s.births,
      deaths: s.deaths,
      fragility: s.fragility,
      events: s.entryCounts.event ?? 0,
      keyLines: narrated(s.keyEntries, game),
    }),
  );
  return {
    charts,
    markers: markersOf(game),
    archive: [...summaries, ...full].reverse(),
    seasons: game.series.population.length,
  };
});
